"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppStudioError = void 0;
const constants_1 = require("./constants");
const localizeUtils_1 = require("../../../common/localizeUtils");
class AppStudioError {
    static FileNotFoundError = {
        name: "FileNotFoundError",
        message: (filePath) => [
            (0, localizeUtils_1.getDefaultString)("error.common.FileNotFoundError", filePath),
            (0, localizeUtils_1.getLocalizedString)("error.common.FileNotFoundError", filePath),
        ],
    };
    static AuthServiceAPIFailedError = {
        name: "AuthServiceAPIFailed",
        message: (e, requestPath, apiName) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.authServiceApiFailed", e.name, e.message, apiName),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.authServiceApiFailed", e.name, e.message, requestPath),
        ],
    };
    static TeamsAppCreateFailedError = {
        name: "TeamsAppCreateFailed",
        message: (error) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppCreateFailed", error?.message),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppCreateFailed", error?.displayMessage),
        ],
    };
    static TeamsAppUpdateFailedError = {
        name: "TeamsAppUpdateFailed",
        message: (teamsAppId, error) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppUpdateFailed", teamsAppId, error.message),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppUpdateFailed", teamsAppId, error.displayMessage),
        ],
    };
    static InvalidTeamsAppIdError = {
        name: "InvalidTeamsAppId",
        message: (teamsAppId) => [
            (0, localizeUtils_1.getDefaultString)("error.teamsApp.InvalidAppIdError", teamsAppId),
            (0, localizeUtils_1.getLocalizedString)("error.teamsApp.InvalidAppIdError", teamsAppId),
        ],
    };
    static TeamsAppNotExistsError = {
        name: "TeasmAppNotExists",
        message: (teamsAppId) => [
            (0, localizeUtils_1.getDefaultString)("error.teamsApp.AppIdNotExistError", teamsAppId),
            (0, localizeUtils_1.getLocalizedString)("error.teamsApp.AppIdNotExistError", teamsAppId),
        ],
    };
    static ValidationFailedError = {
        name: "ManifestValidationFailed",
        message: (errors) => [
            (0, localizeUtils_1.getDefaultString)("plugins.appstudio.validationFailedNotice") + " " + errors.join("\n"),
            (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.validationFailedNotice") + " " + errors.join("\n"),
        ],
    };
    static SyncManifestFailedError = {
        name: "SyncManifestFailed",
        message: (errors) => [
            (0, localizeUtils_1.getDefaultString)("plugins.appstudio.syncManifestFailedNotice") + " " + errors.join("\n"),
            (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.syncManifestFailedNotice") + " " + errors.join("\n"),
        ],
    };
    static TeamsAppPublishFailedError = {
        name: "TeamsAppPublishFailed",
        message: (teamsAppId, requestPath) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.publishFailed", teamsAppId) +
                ` Request path: ${requestPath}`,
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.publishFailed", teamsAppId) +
                ` Request path: ${requestPath}`,
        ],
    };
    static TeamsPackageBuildError = {
        name: "TeamsPackageBuildError",
        message: (error) => [
            error.message ?? (0, localizeUtils_1.getDefaultString)("error.appstudio.buildError"),
            error.displayMessage ?? (0, localizeUtils_1.getLocalizedString)("error.appstudio.buildError"),
        ],
    };
    static CheckPermissionFailedError = {
        name: "CheckPermissionFailed",
        message: (error) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.checkPermissionFailed", error.message),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.checkPermissionFailed", error.message),
        ],
    };
    static GrantPermissionFailedError = {
        name: "GrantPermissionFailed",
        message: (errorMessage, id) => [
            `${constants_1.Constants.PERMISSIONS.name}: ${id}. ` +
                (0, localizeUtils_1.getDefaultString)("error.appstudio.grantPermissionFailed", errorMessage),
            `${constants_1.Constants.PERMISSIONS.name}: ${id}. ` +
                (0, localizeUtils_1.getLocalizedString)("error.appstudio.grantPermissionFailed", errorMessage),
        ],
    };
    static ListCollaboratorFailedError = {
        name: "ListCollaboratorFailedError",
        message: (error) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.listCollaboratorFailed", error.message),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.listCollaboratorFailed", error.message),
        ],
    };
    static UpdateManifestWithInvalidAppError = {
        name: "UpdateManifestWithInvalidAppError",
        message: (appId) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.updateManifestInvalidApp", appId),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.updateManifestInvalidApp", appId),
        ],
    };
    static InvalidCapabilityError = {
        name: "InvalidCapabilityError",
        message: (capability) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.invalidCapability", capability),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.invalidCapability", capability),
        ],
    };
    static CapabilityExceedLimitError = {
        name: "CapabilityExceedLimitError",
        message: (capability) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.capabilityExceedLimit", capability),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.capabilityExceedLimit", capability),
        ],
    };
    static StaticTabNotExistError = {
        name: "StaticTabNotExist",
        message: (index) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.staticTabNotExist", index),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.staticTabNotExist", index),
        ],
    };
    static CapabilityNotExistError = {
        name: "CapabilityNotExist",
        message: (capability) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.capabilityNotExist", capability),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.capabilityNotExist", capability),
        ],
    };
    static TeamsAppCreateConflictError = {
        name: "TeamsAppCreateConflict",
        message: () => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppCreateConflict"),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppCreateConflict"),
        ],
    };
    static TeamsAppCreateConflictWithPublishedAppError = {
        name: "TeamsAppCreateConflictWithPublishedApp",
        message: () => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppCreateConflictWithPublishedApp"),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppCreateConflictWithPublishedApp"),
        ],
    };
    static TeamsAppPublishConflictError = {
        name: "TeamsAppPublishConflict",
        message: (teamsAppId) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppPublishConflict", teamsAppId),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppPublishConflict", teamsAppId),
        ],
    };
    static TeamsAppRequiredPropertyMissingError = {
        name: "TeamsAppMissingRequiredCapability",
        message: (property, path) => [
            (0, localizeUtils_1.getDefaultString)("error.appstudio.teamsAppRequiredPropertyMissing", property, path),
            (0, localizeUtils_1.getLocalizedString)("error.appstudio.teamsAppRequiredPropertyMissing", property, path),
        ],
    };
}
exports.AppStudioError = AppStudioError;
//# sourceMappingURL=errors.js.map